import React from 'react';
import { useSelector } from 'react-redux';
import SecondScreenGallery from './SecondScreenGallery';
import SecondScreenSharedVideo from './SecondScreenSharedVideo';
import SecondScreenSingle from './SecondScreenSingle';
import SecondScreenStage from './SecondScreenStage';
import SecondScreenWhiteboard from './SecondScreenWhiteboard';
/**
 * Routes a single second-screen window to a layout from its redux source
 * descriptor. A {@code whiteboard} source renders the {@link SecondScreenWhiteboard}
 * iframe; a {@code sharedvideo} source renders the meeting's shared video via
 * {@link SecondScreenSharedVideo}; a {@code screenshare} source renders that
 * screenshare full-bleed; a {@code tile} source renders the
 * {@link SecondScreenGallery} grid; a stage or participant source renders the
 * {@link SecondScreenStage} layout (featured participant plus filmstrip); any
 * other source renders the single track/avatar via {@link SecondScreenSingle}.
 * Each layout resolves its own state, so the potentially expensive
 * {@code resolveSource} selector only runs where it is needed. Portaled into
 * the second window by {@link SecondScreenPortals}.
 *
 * @param {IProps} props - The component props.
 * @returns {ReactElement}
 */
const SecondScreenView = ({ id, win }) => {
    const source = useSelector((state) => state['features/multi-screen'].screens[id]?.source);
    if (source?.role === 'whiteboard') {
        return React.createElement(SecondScreenWhiteboard, null);
    }
    if (source?.role === 'sharedvideo') {
        return (React.createElement(SecondScreenSharedVideo, { id: id }));
    }
    // Before the participant check below: a screenshare names its virtual
    // screenshare participant to say which one to show, but it is still shown
    // full-bleed on its own rather than in the stage layout.
    if (source?.role === 'screenshare') {
        return (React.createElement(SecondScreenSingle, { id: id, win: win }));
    }
    if (source?.role === 'tile') {
        return (React.createElement(SecondScreenGallery, { win: win }));
    }
    if (source && (source.role === 'stage' || Boolean(source.participant))) {
        return (React.createElement(SecondScreenStage, { id: id, win: win }));
    }
    return (React.createElement(SecondScreenSingle, { id: id, win: win }));
};
export default SecondScreenView;
